import { useState } from 'react';
import { supabase } from '../../lib/supabase';

interface PatientSimilaire {
  id: string;
  nom: string;
  prenom: string;
  date_naissance: string;
  email?: string;
  telephone?: string;
}

interface CreerPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (patientId: string) => void;
}

export default function CreerPatientModal({ isOpen, onClose, onSuccess }: CreerPatientModalProps) {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    dateNaissance: '',
    email: '',
    telephone: '',
    adresse: '',
    password: '',
    sexe: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingDoublon, setCheckingDoublon] = useState(false);
  const [showDoublonPopup, setShowDoublonPopup] = useState(false);
  const [patientSimilaire, setPatientSimilaire] = useState<PatientSimilaire | null>(null);

  if (!isOpen) return null;

  // Vérifier les doublons potentiels
  const checkForDoublons = async () => {
    if (!formData.nom || !formData.prenom || !formData.dateNaissance) {
      return null;
    }

    setCheckingDoublon(true);
    try {
      // Vérifier par nom, prénom et date de naissance
      const { data: byIdentity } = await supabase
        .from('patients')
        .select('id, nom, prenom, date_naissance, email, telephone')
        .ilike('nom', formData.nom)
        .ilike('prenom', formData.prenom)
        .eq('date_naissance', formData.dateNaissance)
        .maybeSingle();

      if (byIdentity) {
        return byIdentity;
      }

      // Vérifier par email si fourni
      if (formData.email) {
        const { data: byEmail } = await supabase
          .from('patients')
          .select('id, nom, prenom, date_naissance, email, telephone')
          .ilike('email', formData.email)
          .maybeSingle();

        if (byEmail) {
          return byEmail;
        }
      }

      // Vérifier par téléphone si fourni
      if (formData.telephone) {
        const { data: byPhone } = await supabase
          .from('patients')
          .select('id, nom, prenom, date_naissance, email, telephone')
          .eq('telephone', formData.telephone)
          .maybeSingle();

        if (byPhone) {
          return byPhone;
        }
      }

      return null;
    } catch (error) {
      console.error('Erreur lors de la vérification des doublons:', error);
      return null;
    } finally {
      setCheckingDoublon(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.nom || !formData.prenom || !formData.dateNaissance) {
      setError('Nom, prénom et date de naissance sont obligatoires');
      return;
    }

    if (!formData.email && !formData.telephone) {
      setError('Email ou téléphone est obligatoire');
      return;
    }

    if (!formData.password || formData.password.length < 10) {
      setError('Le mot de passe doit contenir au moins 10 caractères');
      return;
    }

    // Vérifier les doublons
    const doublon = await checkForDoublons();
    if (doublon) {
      setPatientSimilaire(doublon);
      setShowDoublonPopup(true);
      return;
    }

    // Procéder à la création
    await proceedWithCreation();
  };

  const proceedWithCreation = async () => {
    setLoading(true);

    try {
      // Appeler la fonction Edge pour créer le compte patient de manière sécurisée
      const { data: result, error: functionError } = await supabase.functions.invoke(
        'create-patient-by-staff',
        {
          body: {
            nom: formData.nom,
            prenom: formData.prenom,
            dateNaissance: formData.dateNaissance,
            email: formData.email || null,
            telephone: formData.telephone || null,
            adresse: formData.adresse || null,
            password: formData.password,
            sexe: formData.sexe || null,
          },
        }
      );

      if (functionError) {
        console.error('Erreur fonction Edge:', functionError);
        
        // Messages d'erreur personnalisés
        if (functionError.message?.includes('User already registered')) {
          throw new Error('Un compte existe déjà avec cet email ou ce numéro de téléphone');
        } else if (functionError.message?.includes('duplicate key')) {
          throw new Error('Un patient avec ces informations existe déjà dans le système');
        } else {
          throw new Error('Erreur lors de la création du compte. Veuillez réessayer.');
        }
      }

      if (result?.error) {
        console.error('Erreur résultat:', result.error);
        
        // Messages d'erreur personnalisés depuis la fonction Edge
        if (result.error.includes('User already registered')) {
          throw new Error('Un compte existe déjà avec cet email ou ce numéro de téléphone');
        } else if (result.error.includes('duplicate key')) {
          throw new Error('Un patient avec ces informations existe déjà dans le système');
        } else if (result.error.includes('email')) {
          throw new Error('L\'adresse email est invalide ou déjà utilisée');
        } else if (result.error.includes('phone')) {
          throw new Error('Le numéro de téléphone est invalide ou déjà utilisé');
        } else {
          throw new Error(result.error);
        }
      }

      onSuccess(result.patient.id);
      handleClose();
    } catch (err: any) {
      console.error('Erreur:', err);
      setError(err.message || 'Une erreur est survenue lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  const handleDoublonResponse = (useExisting: boolean) => {
    if (useExisting && patientSimilaire) {
      // Utiliser le patient existant
      onSuccess(patientSimilaire.id);
      handleClose();
    } else {
      // Continuer avec la création
      setShowDoublonPopup(false);
      setPatientSimilaire(null);
      proceedWithCreation();
    }
  };

  const handleClose = () => {
    setFormData({
      nom: '',
      prenom: '',
      dateNaissance: '',
      email: '',
      telephone: '',
      adresse: '',
      password: '',
      sexe: '',
    });
    setError('');
    setShowDoublonPopup(false);
    setPatientSimilaire(null);
    onClose();
  };

  // Générer un mot de passe temporaire
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b-2 border-gray-100 p-4 sm:p-6 rounded-t-3xl">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-clinic-green/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-add-line text-xl sm:text-2xl text-clinic-green"></i>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg sm:text-2xl font-bold text-clinic-blue-dark truncate">Créer un compte patient</h3>
                  <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">Le patient devra changer son mot de passe sous 24h</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-xl sm:text-2xl"></i>
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {checkingDoublon && (
              <div className="mb-4 p-3 sm:p-4 bg-blue-50 border-2 border-blue-200 rounded-2xl flex items-center gap-3">
                <i className="ri-loader-4-line text-lg sm:text-xl text-blue-600 animate-spin flex-shrink-0"></i>
                <span className="text-sm sm:text-base text-blue-800 font-medium">Vérification des doublons...</span>
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 sm:p-4 bg-red-50 border-2 border-red-200 text-red-600 rounded-2xl text-xs sm:text-sm flex items-start">
                <i className="ri-error-warning-line text-lg sm:text-xl mr-2 mt-0.5 flex-shrink-0"></i>
                <span>{error}</span>
              </div>
            )}

            <div className="mb-6 p-3 sm:p-4 bg-yellow-50 border-2 border-yellow-200 rounded-2xl">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-lg sm:text-xl text-yellow-600 mt-0.5 flex-shrink-0"></i>
                <div className="text-xs sm:text-sm text-yellow-800">
                  <p className="font-semibold mb-1">Important - Sécurité du compte</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Le patient recevra ses identifiants de connexion</li>
                    <li>Il devra obligatoirement changer son mot de passe dans les 24h</li>
                    <li>Après 24h, le compte sera bloqué jusqu'au changement de mot de passe</li>
                  </ul>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-clinic-blue-dark mb-2">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    placeholder="Nom du patient"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-clinic-blue-dark mb-2">
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    placeholder="Prénom du patient"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-clinic-blue-dark mb-2">Sexe</label>
                <select
                  value={formData.sexe}
                  onChange={(e) => setFormData({ ...formData, sexe: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm pr-8"
                >
                  <option value="">Sélectionner</option>
                  <option value="Masculin">Masculin</option>
                  <option value="Féminin">Féminin</option>
                </select>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-clinic-blue-dark mb-2">
                  Date de naissance <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={formData.dateNaissance}
                  onChange={(e) => setFormData({ ...formData, dateNaissance: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-clinic-blue-dark mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    placeholder="email@exemple.com"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-clinic-blue-dark mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm"
                    placeholder="+229 XX XX XX XX"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-clinic-blue-dark mb-2">
                  Adresse
                </label>
                <textarea
                  value={formData.adresse}
                  onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                  rows={2}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
                  placeholder="Adresse complète du patient"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-clinic-blue-dark mb-2">
                  Mot de passe temporaire <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border-2 border-clinic-blue/20 rounded-2xl focus:outline-none focus:border-clinic-blue text-sm font-mono"
                    placeholder="Minimum 10 caractères"
                  />
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="px-4 py-2 sm:py-3 bg-clinic-blue/10 text-clinic-blue rounded-2xl hover:bg-clinic-blue/20 transition-all whitespace-nowrap cursor-pointer text-sm"
                  >
                    <i className="ri-refresh-line mr-2"></i>
                    Générer
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  <i className="ri-information-line mr-1"></i>
                  Ce mot de passe sera communiqué au patient. Il devra le changer sous 24h.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 sm:px-6 py-2 sm:py-3 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all font-semibold whitespace-nowrap cursor-pointer text-sm"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading || checkingDoublon}
                  className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-clinic-green text-white rounded-full hover:bg-clinic-green/90 transition-all font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm"
                >
                  {loading ? 'Création...' : 'Créer le compte'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Popup de confirmation de doublon */}
      {showDoublonPopup && patientSimilaire && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8">
            <div className="text-center mb-6">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-alert-line text-2xl sm:text-3xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Patient existant détecté</h3>
              <p className="text-sm sm:text-base text-gray-600">Un patient avec des informations similaires existe déjà</p>
            </div>

            <div className="bg-gray-50 p-4 sm:p-6 rounded-2xl mb-6 space-y-3">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Nom complet</p>
                <p className="font-semibold text-gray-900 text-sm sm:text-base">{patientSimilaire.nom} {patientSimilaire.prenom}</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Date de naissance</p>
                <p className="font-semibold text-gray-900 text-sm sm:text-base">
                  {new Date(patientSimilaire.date_naissance + 'T00:00:00').toLocaleDateString('fr-FR')}
                </p>
              </div>
              {patientSimilaire.email && (
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Email</p>
                  <p className="font-semibold text-gray-900 text-sm sm:text-base break-all">{patientSimilaire.email}</p>
                </div>
              )}
              {patientSimilaire.telephone && (
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Téléphone</p>
                  <p className="font-semibold text-gray-900 text-sm sm:text-base">{patientSimilaire.telephone}</p>
                </div>
              )}
            </div>

            <div className="mb-6 p-3 sm:p-4 bg-blue-50 border-2 border-blue-200 rounded-2xl">
              <p className="text-xs sm:text-sm text-blue-800 text-center">
                <i className="ri-question-line mr-2"></i>
                Voulez-vous utiliser ce patient existant ?
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => handleDoublonResponse(true)}
                className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-check-line mr-2"></i>
                Oui, utiliser ce patient
              </button>
              <button
                onClick={() => handleDoublonResponse(false)}
                className="w-full px-4 sm:px-6 py-3 sm:py-4 border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all font-semibold whitespace-nowrap cursor-pointer text-sm"
              >
                <i className="ri-close-line mr-2"></i>
                Non, créer un nouveau patient
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
